import React, { useCallback, useRef, useState } from 'react';
import { capitalize } from 'lodash';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/nextjs';

import Header from '../../../../../components/checkout/Header';
import CenteredContainer from 'components/CenteredContainer';
import { useViewEvent } from 'features/events/context/ViewEvent';
import { CheckoutSteps } from '../../types';
import EventInfo from '../EventInfo';
import Button from 'components/Button';
import { useCheckout } from '../../context';
import TopBackgroundWrapper from 'components/checkout/TopBackgroundWrapper';
import PrePurchaseQuestions from 'features/forms/components/events/PrePurchaseQuestions';
import CheckoutTimer from '../CheckoutTimer';
import {
  useCreatePrePurchaseResponseMutation,
  useUpdatePrePurchaseResponseMutation
} from 'api/queries/events/pre-purchase-questions';
import { error } from 'legacy/common/utils/toast';

const PrePurchaseQuestionsStep = () => {
  const { t } = useTranslation(['checkout']);
  const [canSubmit, setCanSubmit] = useState(false);
  const { event } = useViewEvent();
  const {
    checkoutSession: {
      checkoutSession,
      confirmCheckout,
      isLoadingCheckout,
      checkoutError,
      isCheckoutDisabled
    },
    user,
    waivers,
    onSuccess,
    paymentMethod,
    goToStep
  } = useCheckout();
  const {
    mutateAsync: createPrePurchaseResponse,
    isLoading: isCreatingPrePurchaseResponse
  } = useCreatePrePurchaseResponseMutation();
  const {
    mutateAsync: updatePrePurchaseResponse,
    isLoading: isUpdatingPrePurchaseResponse
  } = useUpdatePrePurchaseResponseMutation();

  const prePurchaseQuestionsRef = useRef<{
    submit: () => void;
    reset: () => void;
  }>(null);

  const goBack = () => {
    if (waivers.data && waivers.data.length > 0) {
      goToStep(CheckoutSteps.Waivers);
    } else {
      goToStep(CheckoutSteps.Summary);
    }
  };

  const onConfirmPrePurchaseQuestions = () => {
    prePurchaseQuestionsRef?.current?.submit();
  };

  const getButtonText = () => {
    if (checkoutSession?.total === 0) {
      return t('checkout:confirmAndRsvp');
    }

    if (paymentMethod.selectedBuyNowPayLater.value) {
      return t('checkout:continueTo', {
        paymentMethod: capitalize(paymentMethod.selectedBuyNowPayLater.value)
      });
    }

    return t('checkout:confirmAndPay');
  };

  const handleSetCanSubmit = useCallback((canSubmit: boolean) => {
    setCanSubmit(canSubmit);
  }, []);

  const onComplete = useCallback(
    async ({
      data,
      isEditing
    }: {
      data: Record<string, any>;
      isEditing: boolean;
    }) => {
      try {
        const response = await (isEditing
          ? updatePrePurchaseResponse
          : createPrePurchaseResponse)({
          eventId: event._id,
          response: { response: data }
        });

        if (response.responderId) {
          confirmCheckout({
            allWaiversAccepted: waivers.allWaiversAccepted
          })
            .then(() => {
              onSuccess();
              goToStep(CheckoutSteps.Success);
            })
            .catch((err) => {
              error(err.message || 'Error confirming checkout');

              Sentry.captureException(err, {
                tags: {
                  component: 'CheckoutConfirmCheckoutPostPrePurchaseQuestions',
                  action: 'onComplete'
                }
              });
            });
        }
      } catch (err) {
        prePurchaseQuestionsRef?.current?.reset();
        error(err.message || 'Error creating pre purchase response');

        Sentry.captureException(err, {
          tags: {
            component: 'CheckoutPrePurchaseQuestionsStep',
            action: 'onComplete'
          }
        });
      }
    },
    [event._id]
  );

  return (
    <>
      <Header
        onBack={goBack}
        title={t('checkout:prePurchaseQuestions')}
        subtitle={event.title}
        isUserLoggedIn={!!user}
        TimerComponent={CheckoutTimer}
      />
      <TopBackgroundWrapper
        imageUrl={event.photo.url}
        hasTierNav={false}
        isUserLoggedIn={!!user}
        className="md:py-14"
      >
        <EventInfo />
      </TopBackgroundWrapper>
      <CenteredContainer hasPadding={false} wideContent>
        <div className="px-4 gap-5 md:gap-8 flex flex-col pb-10">
          <div className="max-w-[650px] mx-auto flex flex-col gap-5 pt-6">
            <PrePurchaseQuestions
              ref={prePurchaseQuestionsRef}
              eventId={event._id}
              prePurchaseFormId={event.prePurchaseFormId}
              handleComplete={onComplete}
              handleSetCanSubmit={handleSetCanSubmit}
            />
            {checkoutError?.message && (
              <div className="text-error text-center pb-2">
                {checkoutError.message}
              </div>
            )}
            {canSubmit && (
              <Button
                variant="primaryDark"
                onClick={onConfirmPrePurchaseQuestions}
                size="large"
                loading={
                  isCreatingPrePurchaseResponse ||
                  isUpdatingPrePurchaseResponse ||
                  isLoadingCheckout
                }
                className="w-full"
                disabled={isCheckoutDisabled}
              >
                {getButtonText()}
              </Button>
            )}
          </div>
        </div>
      </CenteredContainer>
    </>
  );
};

export default PrePurchaseQuestionsStep;
